/**
 * Утилиты для работы с медиа треками
 * Изолированная логика работы с MediaStreamTrack
 */

import type { QualityProfile } from '../types';

/**
 * Получает видео трек из потока
 */
export function getVideoTrack(stream: MediaStream | null): MediaStreamTrack | null {
  if (!stream) return null;
  const tracks = stream.getVideoTracks();
  return tracks.length > 0 ? tracks[0] : null;
}

/**
 * Получает аудио трек из потока
 */
export function getAudioTrack(stream: MediaStream | null): MediaStreamTrack | null {
  if (!stream) return null;
  const tracks = stream.getAudioTracks();
  return tracks.length > 0 ? tracks[0] : null;
}

/**
 * Останавливает все треки в потоке
 */
export function stopAllTracks(stream: MediaStream | null): void {
  if (!stream) return;
  stream.getTracks().forEach(track => track.stop());
}

/**
 * Останавливает треки определенного типа
 */
export function stopTracksByKind(stream: MediaStream | null, kind: 'audio' | 'video'): void {
  if (!stream) return;
  stream.getTracks()
    .filter(track => track.kind === kind)
    .forEach(track => track.stop());
}

/**
 * Включает или выключает треки определенного типа
 */
export function setTracksEnabled(stream: MediaStream | null, kind: 'audio' | 'video', enabled: boolean): void {
  if (!stream) return;
  stream.getTracks()
    .filter(track => track.kind === kind)
    .forEach(track => track.enabled = enabled);
}

/**
 * Применяет качество к видео треку
 */
export async function applyQualityToTrack(track: MediaStreamTrack, profile: QualityProfile): Promise<boolean> {
  try {
    const constraints: MediaTrackConstraints = {
      width: { ideal: profile.width },
      height: { ideal: profile.height },
      frameRate: { ideal: profile.frameRate }
    };

    await track.applyConstraints(constraints);
    return true;
  } catch (error) {
    console.error('❌ Ошибка применения качества к треку:', error);
    return false;
  }
}

/**
 * Копирует аудио трек из одного потока в другой
 */
export function copyAudioTrack(from: MediaStream | null, to: MediaStream | null): void {
  if (!from || !to) return;
  const audioTrack = getAudioTrack(from);
  if (audioTrack && audioTrack.readyState === 'live' && !getAudioTrack(to)) {
    to.addTrack(audioTrack);
  }
}

/**
 * Получает параметры видео трека
 */
export function getVideoSettings(track: MediaStreamTrack | null): MediaTrackSettings | null {
  return track ? track.getSettings() : null;
}

/**
 * Получает capabilities видео трека
 */
export function getVideoCapabilities(track: MediaStreamTrack | null): MediaTrackCapabilities | null {
  return track ? track.getCapabilities() : null;
}

